import { useQuery } from "@tanstack/react-query";

import {
  getContractStats,
  type ContractPlatform,
  type ContractRevision,
  type ContractShim,
  type ContractStats,
} from "./api";

/**
 * The derivations and words behind Overview's two client-contract cards, in a
 * module of their own: the cards' file may export components and nothing else
 * (react-refresh/only-export-components).
 */

/** Hangs off the project prefix like every other thing a project owns. */
export function contractStatsQueryKey(projectId: string) {
  return ["project", projectId, "contract-stats"] as const;
}

/**
 * One query behind both cards. They live in different columns of the grid and
 * ask separately; the shared key is what makes them one request and, more to
 * the point, one denominator — a shim's share is a fraction of the population
 * the revision rows add up to.
 */
export function useContractStats(projectId: string) {
  return useQuery<ContractStats>({
    queryKey: contractStatsQueryKey(projectId),
    queryFn: () => getContractStats(projectId),
    // No `enabled` guard on purpose. Gated on an empty id the query would sit
    // in `pending` forever, and the cards read «no data yet» as «still
    // loading» — an id that never arrives would show a spinner that never
    // stops, which reads as slow rather than as broken. The route always
    // supplies one; if that ever stops being true, a failed request says so
    // and the error strip offers Retry.
  });
}

/**
 * The window the card announces before the server has told it one — the same
 * thirty days `internal/contract.MaxWindowDays` derives from the aggregate's
 * TTL. Only ever on screen while the answer is in flight.
 */
export const CONTRACT_WINDOW_DAYS = 30;

/**
 * The wire order is the history: oldest first, which is how the registry file
 * and the aggregate's own labels are both read. The card is not a history — it
 * is opened to answer «where is everybody now» — so the newest entry goes at
 * the top and the past falls away below it. The mockup draws both lists that
 * way.
 *
 * For the shim list a plain reversal IS that order. Revision rows need
 * `revisionRowsNewestFirst` instead — see there.
 */
export function newestFirst<T>(rows: readonly T[]): T[] {
  return rows.slice().reverse();
}

/**
 * Revision rows, current shape first.
 *
 * A plain reversal would not do it: the wire ends with the `future` bucket —
 * everything above the revision this server has a name for — so reversing puts
 * that on top. And `ahead` is usually one developer on a pre-release build,
 * which would push «Current contract» off the head of both the legend and the
 * bar over a single player.
 *
 * Current, then whoever is ahead of it, then the past newest-first.
 */
export function revisionRowsNewestFirst<T extends { standing: string }>(
  rows: readonly T[],
): T[] {
  const rank = (r: T) => (r.standing === "current" ? 0 : r.standing === "ahead" ? 1 : 2);
  return rows
    .map((row, i) => ({ row, i }))
    .sort((a, b) => rank(a.row) - rank(b.row) || b.i - a.i)
    .map(({ row }) => row);
}

/** Thousands separated, the way every count on this page is written. */
export function formatCount(n: number): string {
  return n.toLocaleString("en-US");
}

/**
 * One decimal — except at the two ends, where the decimal would be a lie.
 *
 * Zero is the single value that green-lights deleting a shim, so it is the one
 * number here that has to be unmistakable. It prints as a bare «0 %», and
 * anything under a tenth of a percent prints as a floor rather than rounding
 * down into it: one player in three thousand shown as "0.0 %" is the exact
 * mistake this card exists to prevent.
 */
export function formatShare(share: number): string {
  const pct = share * 100;
  if (pct <= 0) return "0 %";
  if (pct < 0.1) return "< 0.1 %";
  return `${pct.toFixed(1)} %`;
}

/** The denominator: everybody seen over the window, on every revision. */
export function totalPlayers(revisions: ContractRevision[]): number {
  return revisions.reduce((sum, r) => sum + r.players, 0);
}

/** A row's share of that population. */
export function revisionShare(
  revision: ContractRevision,
  total: number,
): number {
  return total > 0 ? revision.players / total : 0;
}

/**
 * The colour a revision is drawn in, in the bar and on its badge.
 *
 * Four tones and not one per row: the current shape is the accent, revision 0
 * is the one that carries a warning because it is both the oldest shape there
 * is and every caller who said nothing, anything ahead of this server is news
 * rather than debt, and the revisions in between are one colour — the bar's
 * job is to show how much is not current, and the legend names each row.
 */
export type RevisionTone = "current" | "zero" | "legacy" | "ahead";

export function revisionTone(revision: ContractRevision): RevisionTone {
  if (revision.standing === "current") return "current";
  if (revision.standing === "ahead") return "ahead";
  return revision.label === "0" ? "zero" : "legacy";
}

/** The badge: `rev 2`, or the one label everything too far ahead shares. */
export function revisionBadge(revision: ContractRevision): string {
  return revision.label === "future" ? "future" : `rev ${revision.label}`;
}

/** The row's own line in the legend. */
export function revisionName(revision: ContractRevision): string {
  return revision.label === "future"
    ? "Ahead of this server"
    : `Revision ${revision.label}`;
}

/** What this revision is, in one line under the badge. */
export function revisionWhat(revision: ContractRevision): string {
  if (revision.standing === "current") return "Current contract";
  if (revision.standing === "ahead") return "Newer than this server";
  // Revision 0 is not only the oldest shape: it is also every caller who sent
  // no header at all. That is worth more than a shim count, which is why the
  // count gives way here.
  if (revision.label === "0") {
    return "Builds older than the mechanism and requests with no header";
  }
  if (revision.shims === 1) return "Served by 1 shim";
  if (revision.shims > 1) return `Served by ${revision.shims} shims`;
  return "Behind the current contract";
}

/**
 * The SDK versions under a revision, or why there are none.
 *
 * The two silences are different and both are ordinary. Nobody was here at all,
 * or somebody was and named no build — which is what curl and a game backend on
 * the S2S routes look like, and both of those land on revision 0.
 */
export function revisionVersions(
  revision: ContractRevision,
  stats: ContractStats,
): { text: string; quiet: boolean } {
  if (revision.sdk_versions.length > 0) {
    return { text: `SDK ${revision.sdk_versions.join(" · ")}`, quiet: false };
  }
  if (revision.players === 0) {
    // «Nothing in thirty days» is a claim about thirty days. On an aggregate
    // that has been watching for six of them it is false — and false in the
    // direction that retires a live shim, which is the one direction that
    // costs anything here.
    return {
      text: stats.window_covered
        ? `no requests in ${stats.window_days} days`
        : `no requests since ${formatObservedSince(stats.observed_since)}`,
      quiet: true,
    };
  }
  return { text: "no SDK version reported", quiet: true };
}

/**
 * The day the aggregate started watching, in the panel's own date format, or
 * «—» when it holds no mark at all.
 */
const OBSERVED_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

export function formatObservedSince(day: string): string {
  if (!day) return "—";
  // The server sends a UTC calendar day; parsed bare it would shift a day
  // backwards for anybody west of Greenwich.
  const d = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return "—";
  return OBSERVED_FMT.format(d);
}

/**
 * The verdict on one entry of the registry.
 *
 * Neither `unknown` nor `tooSoon` is a shade of `removable`. A store that did
 * not answer, an aggregate younger than the window, and a revision nobody has
 * been on for a month arrive as the same zero everywhere else in a UI; here
 * they must not, because only the last one deletes code that nothing needs.
 *
 * `needed` is decided BEFORE coverage, and deliberately: a positive count is
 * positive whatever the aggregate has and has not seen — a short history can
 * only ever undercount — so a shim that is demonstrably in use says so even on
 * a store that came up yesterday.
 */
export type ShimVerdict = "unknown" | "tooSoon" | "removable" | "needed";

export function shimVerdict(shim: ContractShim, platform: ContractPlatform): ShimVerdict {
  if (!platform.available || shim.platform_players === null) return "unknown";
  if (shim.platform_players > 0) return "needed";
  return platform.window_covered ? "removable" : "tooSoon";
}

/**
 * The share of the PLATFORM's players this shim is still serving — the same
 * number the verdict beside it is taken from, and the one RetireBelow is
 * compared with. A threshold standing on a different population than the
 * verdict is worse than no threshold.
 */
export function shimShare(shim: ContractShim, platform: ContractPlatform): number {
  if (shim.platform_players === null || platform.players <= 0) return 0;
  return shim.platform_players / platform.players;
}

/**
 * Whether this entry has fallen under the share it declared it stops paying at.
 *
 * Not a verdict and never drawn as one — deletion has exactly one condition and
 * it is an honest zero across the whole window. This is the other thing the
 * number was written down for: the point at which somebody goes and asks the
 * game teams to ship a new build.
 */
export function belowBreakEven(shim: ContractShim, platform: ContractPlatform): boolean {
  if (
    shim.retire_below <= 0 ||
    shim.platform_players === null ||
    shim.platform_players === 0
  ) {
    return false;
  }
  return shimShare(shim, platform) < shim.retire_below;
}
